import { useEffect, useState } from 'react';
import { api, type OfficeBillingStatement } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { money, period as writtenPeriod } from '../format';
import { ErrorAlert, failureOf, type Failure } from './Alerts';

/** Domyślnie poprzedni miesiąc — ten, który właśnie się rozlicza. */
function previousMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
}

/**
 * Rozliczenie biura z Firmeto (E11-S3): ile biuro płaci za firmy z pakietu, ile oszczędził mu
 * sufit i ile dochodzi z umowy indywidualnej. To ta sama kwota, którą obciążamy biuro.
 */
export function OfficeBillingPanel({ officeId, t, locale }: { officeId: string; t: Dictionary; locale: Locale }) {
  const [month, setMonth] = useState(previousMonth);
  const [statement, setStatement] = useState<OfficeBillingStatement | null>(null);
  const [error, setError] = useState<Failure | null>(null);

  useEffect(() => {
    setError(null);
    api
      .officeBilling(officeId, month)
      .then(setStatement)
      .catch((failure) => {
        setStatement(null);
        setError(failureOf(failure));
      });
  }, [officeId, month]);

  return (
    <div className="panel">
      <header>
        <h4>{t.payments.officeTitle}</h4>
        <input
          className="inp w"
          type="month"
          aria-label={t.billing.month}
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
      </header>
      <p className="muted sm">{t.payments.officeLead}</p>
      <ErrorAlert error={error} t={t} />
      {statement && (
        <dl className="brk" style={{ maxWidth: 520 }}>
          <dt>
            {t.payments.officeSubtotal} · {t.payments.officeCompanies(statement.rows.length)} · {writtenPeriod(month, locale)}
          </dt>
          <dd>{money(statement.subtotalGross, locale)}</dd>
          {statement.ceilingGross && (
            <>
              <dt>{t.payments.officeCeiling}</dt>
              <dd>{Number(statement.cappedBy) > 0 ? `−${money(statement.cappedBy, locale)}` : money(statement.ceilingGross, locale)}</dd>
            </>
          )}
          {Number(statement.agreementFeeGross) > 0 && (
            <>
              <dt>
                {t.payments.officeAgreement}
                {statement.agreementReference ? ` · ${statement.agreementReference}` : ''}
              </dt>
              <dd>{money(statement.agreementFeeGross, locale)}</dd>
            </>
          )}
          <dt className="tot">{t.payments.officeTotal}</dt>
          <dd className="tot">{money(statement.totalGross, locale)}</dd>
        </dl>
      )}
    </div>
  );
}
